/**
 * JSend is used to standardize the format of JSON responses.
 * - The Order Service API uses JSend.
 * - The responses can inspected to handle the different cases, specially error feedback.
 * - The JSendResponse type is a union of the three possible response types.
 *
 * @see https://github.com/omniti-labs/jsend
 */
import { ErrorCodes, ErrorType, type ErrorsGroup } from '@src/utils/constants.util'

export type JSendResponse = JSendSuccessResponse | JSendFailResponse | JSendErrorResponse

export interface JSendSuccessResponse {
  status: 'success'
  data: any
}

export interface JSendFailResponse {
  status: 'fail'
  data: {
    code: string
    message: string
    errors: Array<{
      code: string
      message: string
      additionalDetails: any
    }>
  }
}

export interface JSendErrorResponse {
  status: 'error'
  message: string
}

export class JSendFailure extends Error {
  code: string
  errors: JSendFailResponse['data']['errors']
  constructor({ data: { message, code, errors, ...rest } }: JSendFailResponse) {
    super(message)
    this.code = code
    this.errors = errors
    Object.assign(this, { ...rest })
  }
}

export class JSendError extends Error {
  message: string
  constructor({ message, ...rest }: JSendErrorResponse) {
    super(message)
    this.message = message
    Object.assign(this, { ...rest })
  }
}

export function isJSend(response: any): response is JSendResponse {
  return (
    response &&
    typeof response === 'object' &&
    'status' in response &&
    ['success', 'fail', 'error'].includes(response.status)
  )
}

export function isJSendSuccess(response: any): response is JSendSuccessResponse {
  return isJSend(response) && response.status === 'success'
}

export function isJSendError(response: any): response is JSendErrorResponse | JSendFailResponse {
  return isJSend(response) && response.status !== 'success'
}

export function isJSendErrorInstance(error: any): error is JSendFailure | JSendError {
  return error instanceof JSendFailure || error instanceof JSendError
}

export function createJSendResponse(
  data: JSendSuccessResponse | JSendFailResponse | JSendErrorResponse
) {
  return data
}

export function createJSendFailure(args: {
  code?: string
  message?: string
  additionalDetails?: any
}) {
  const code = args.code ?? ErrorCodes.Unknown
  const message = args.message ?? 'Unknown error'
  const errors = [{ code, message, additionalDetails: args.additionalDetails ?? null }]
  return new JSendFailure({ status: 'fail', data: { code, message, errors } })
}

export function getJSendErrorCode(error: JSendFailure | Error) {
  if (error instanceof JSendFailure) {
    return (error?.errors?.[0]?.code || error?.code || ErrorCodes.Unknown) as ErrorCodes
  } else {
    return ErrorCodes.Unknown
  }
}

export function getJSendErrorMessage(error: JSendFailure | Error) {
  if (error instanceof JSendFailure) {
    return error?.errors?.[0]?.message || error?.message
  } else {
    return error.message
  }
}

export function getJSendErrorType(error: JSendFailure | Error, errorsGroup: ErrorsGroup | null) {
  const code = getJSendErrorCode(error)
  const type = Object.entries(errorsGroup ?? {}).find(([, codes]) => codes.includes(code))?.[0]
  return (type ?? ErrorType.Unknown) as ErrorType
}

export function getJSendErrorAdditionalDetails(error: JSendFailure | Error) {
  if (error instanceof JSendFailure) {
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    return error?.errors?.[0]?.additionalDetails || null
  } else {
    return null
  }
}

// Get all the error information in a structured way that is serializable (server to client).
export function getJSendErrorInfo(
  error: JSendFailure | Error | null,
  errorsGroup?: ErrorsGroup | null
) {
  return {
    error: error ? { ...error } : null,
    code: error ? getJSendErrorCode(error) : null,
    message: error ? getJSendErrorMessage(error) : null,
    type: error ? getJSendErrorType(error, errorsGroup ?? null) : null,
    additionalDetails: error ? getJSendErrorAdditionalDetails(error) : null,
  }
}

export function parseJSendResponse<T>(response: JSendResponse) {
  if (response.status === 'success') {
    return response.data as T
  } else {
    return response
  }
}
